import { useEffect, useState } from 'react';
import {
  ServiceRequestFormContextValue,
  useServiceRequestFormContext,
} from 'src/components/ServiceRequestForm/CustomHooks/ServiceRequestFormContext';

interface SelectOption {
  label: string;
  value: string;
}

export interface SuiteSelectOption {
  value: string;
  label: string;
  number: string;
  warrantyAddress: string;
  stratalot: string;
  buildingName: string;
}

export interface UseServiceRequestFormSuiteOrchestratorReturn {
  propertyOptions: SelectOption[];
  suiteOptions: SuiteSelectOption[];
  selectedPropertyOption: SelectOption | null;
  selectedSuiteOption: SuiteSelectOption | null;
  selectedProperty: any | null;
  selectedSuite: any | null;
  selectedSuiteAddress: string;
  selectedSuiteBuildingName: string;
  isFetchingSuites: boolean;
  propertyDisabled: boolean;
  hasSelectedProperty: boolean;
  canProceed: boolean;
  onSelectProperty: (option: SelectOption) => Promise<void>;
  onSelectSuite: (option: SuiteSelectOption) => void;
  onNext: () => void;
  onExit: () => void;
}

export const useServiceRequestFormSuiteOrchestrator =
  (): UseServiceRequestFormSuiteOrchestratorReturn => {
    const {
      formData,
      patchFormData,
      goNext,
      user,
      from,
      propertyData,
      propertyDisabled,
      onExit,
      reference,
    }: ServiceRequestFormContextValue = useServiceRequestFormContext();

    const {
      properties,
      propertySuites,
      loadPropertySuites,
      loadPropertyBuildings,
      getBuildingName,
    } = reference;

    const [isFetchingSuites, setIsFetchingSuites] = useState<boolean>(false);

    const loadSuitesAndBuildings = async (propertyId: string): Promise<void> => {
      await Promise.all([
        loadPropertySuites(propertyId),
        loadPropertyBuildings(propertyId),
      ]);
    };

    useEffect(() => {
      if (from === 'HomeOwnerDashboard' && propertyData) {
        patchFormData({
          property_id: propertyData.propertyid,
          propertyId: propertyData.propertyId,
          suiteId: propertyData.suiteId,
          suite_id: propertyData.suite_id,
          serviceRequestType: 'Suite',
        });

        if (propertyData.propertyid) {
          void loadSuitesAndBuildings(propertyData.propertyid);
        }

        return;
      }

      if (!formData.serviceRequestType) {
        patchFormData({ serviceRequestType: 'Suite' });
      }

      if (formData.property_id) {
        void loadSuitesAndBuildings(formData.property_id);
      }
    }, []);

    const toSuiteOption = (suite: any): SuiteSelectOption => {
      return {
        value: suite.id,
        label: suite.number,
        number: suite.number,
        warrantyAddress: suite.warrantyAddress ?? '',
        stratalot: suite.stratalot ?? '',
        buildingName: getBuildingName(suite.buildingId),
      };
    };

    const propertyOptions: SelectOption[] = properties.map((property: any) => ({
      label: property.name,
      value: property.id,
    }));

    const suiteOptions: SuiteSelectOption[] = formData.property_id
      ? propertySuites.map((suite: any) => toSuiteOption(suite))
      : [];

    const selectedProperty: any | null =
      properties.find((property: any) => property.id === formData.property_id) ??
      null;

    const selectedSuite: any | null =
      propertySuites.find((suite: any) => suite.id === formData.suite_id) ?? null;

    const selectedSuiteAddress: string = selectedSuite?.warrantyAddress ?? '';

    const selectedSuiteBuildingName: string = getBuildingName(
      selectedSuite?.buildingId
    );

    const selectedPropertyOption: SelectOption | null = formData.property_id
      ? (propertyOptions.find(
          (option: SelectOption) => option.value === formData.property_id
        ) ?? null)
      : null;

    const selectedSuiteOption: SuiteSelectOption | null = formData.suite_id
      ? (suiteOptions.find(
          (option: SuiteSelectOption) => option.value === formData.suite_id
        ) ?? null)
      : null;

    const onSelectProperty = async (option: SelectOption): Promise<void> => {
      const base = {
        property_id: option.value,
        propertyId: option.value,
        serviceRequestType: 'Suite',
        asset_item_id: '',
        asset_item_nameDescription: '',
        assetId: '',
      };

      const nextFormData = formData.suiteId
        ? { ...base, suite_id: '', suiteId: '' }
        : base;

      patchFormData(nextFormData);

      setIsFetchingSuites(true);
      await loadSuitesAndBuildings(option.value);
      setIsFetchingSuites(false);
    };

    const onSelectSuite = (option: SuiteSelectOption): void => {
      const selected: any = propertySuites.find(
        (suite: any) => suite.id === option.value
      );

      patchFormData({
        suite_id: option.value,
        suiteId: option.value,
        suiteFloorPLan: selected?.floorPlanURL,
        annotatateImage: false,
        asset_item_id: '',
        asset_item_nameDescription: '',
        assetId: '',
      });
    };

    const onNext = (): void => {
      patchFormData({ creatorId: user?.id });
      goNext();
    };

    return {
      propertyOptions,
      suiteOptions,
      selectedPropertyOption,
      selectedSuiteOption,
      selectedProperty,
      selectedSuite,
      selectedSuiteAddress,
      selectedSuiteBuildingName,
      isFetchingSuites,
      propertyDisabled: propertyDisabled ?? false,
      hasSelectedProperty: Boolean(formData.property_id),
      canProceed: Boolean(formData.suite_id),
      onSelectProperty,
      onSelectSuite,
      onNext,
      onExit,
    };
  };